import AddIcon from '@mui/icons-material/Add';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import Box from '@mui/material/Box';
import React from 'react';

import { colors } from './KestrixDesignSystem.jsx';
import { colorTokens } from './tokens/colors.ts';

/**
 * Kestrix SVG Icons - Consolidated custom SVG icons for the KestrixSurvey project
 * 
 * This file contains all custom SVG icons used throughout the application,
 * converted to reusable React components with consistent props and styling.
 * 
 * USAGE:
 * 
 * 1. Import the icons you need:
 *    import { KestrixHomeIcon, KestrixWallIcon } from '@shared';
 * 
 * 2. Use them in your components:
 *    <KestrixHomeIcon size={24} color={colors.primary[500]} />
 * 
 * PROPS:
 * - size: number (default varies by icon) - Size in pixels
 * - color: string (default varies by icon) - Color of the icon
 * - ...props: All other props are passed to the underlying Box component
 * 
 * ICON CATEGORIES:
 * 
 * Building & Property Icons:
 * - KestrixHomeIcon: House/home icon
 * - KestrixWallIcon: Building wall/construction icon  
 * - KestrixDoorIcon: Door/entrance icon
 * - KestrixWindowIcon: Window icon
 * - KestrixRoofIcon: Roof icon
 * 
 * Utility & Feature Icons:
 * - KestrixTemperatureIcon: Thermometer/temperature icon
 * - KestrixQuestionIcon: Help/question mark icon
 * - KestrixGroupIcon: Group/collection icon
 * 
 * EXAMPLES:
 * 
 * // Basic usage
 * <KestrixHomeIcon />
 * 
 * // Custom size and color
 * <KestrixWallIcon size={32} color={colors.semantic.success.main} />
 * 
 * // In a button
 * <KestrixButton icon={<KestrixHomeIcon size={20} />}>
 *   Home
 * </KestrixButton>
 * 
 * // In a chip
 * <KestrixChip label="Temperature" icon={<KestrixTemperatureIcon size={16} />} />
 * 
 * // With custom styling
 * <KestrixHomeIcon 
 *   size={24} 
 *   color={colors.primary[500]} 
 *   sx={{ cursor: 'pointer' }}
 *   onClick={() => console.log('Home clicked')}
 * />
 */

/**
 * Checkmark glyph path data, drawn against a 20x20 viewBox.
 *
 * Exported as raw path data rather than a component so it can also be embedded
 * in CSS backgrounds, where a React element cannot be used.
 */
export const KESTRIX_CHECKMARK_PATH =
  'M15 6.5c-.28 0-.53.11-.71.29L8.5 12.59l-2.79-2.79a1.003 1.003 0 00-1.42 1.42l3.5 3.5c.18.18.43.29.71.29s.53-.11.71-.29l6-6A1.003 1.003 0 0015 6.5z';

/**
 * Builds a CSS `background-image` value holding an inline SVG glyph.
 *
 * @param {string} pathD - Path data drawn against a 20x20 viewBox.
 * @param {string} fill - Glyph fill colour as a CSS hex value.
 * @returns {string} A `url("data:image/svg+xml,...")` value.
 */
export const kestrixSvgBackgroundImage = (pathD, fill) =>
  `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='${pathD}' fill='${encodeURIComponent(fill)}'/%3E%3C/svg%3E")`;

/**
 * Kestrix Home Icon - House/home icon
 */
const KestrixHomeIcon = ({ size = 24, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 25 24"
    fill="none"
    {...props}
  >
    <path 
      d="M9.14062 22V12H15.1406V22M3.14062 9L12.1406 2L21.1406 9V20C21.1406 20.5304 20.9299 21.0391 20.5548 21.4142C20.1798 21.7893 19.6711 22 19.1406 22H5.14062C4.61019 22 4.10148 21.7893 3.72641 21.4142C3.35134 21.0391 3.14063 20.5304 3.14062 20V9Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </Box>
);

/**
 * Kestrix Question Icon - Help/question mark icon
 */
const KestrixQuestionIcon = ({ size = 24, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path 
      d="M21.38 0.856445H2.52288C1.57611 0.856445 0.808594 1.62396 0.808594 2.57073L0.808594 21.4279C0.808594 22.3746 1.57611 23.1422 2.52288 23.1422H21.38C22.3268 23.1422 23.0943 22.3746 23.0943 21.4279L23.0943 2.57073C23.0943 1.62396 22.3268 0.856445 21.38 0.856445Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M9.37988 9.42788C9.37988 8.91929 9.53069 8.42214 9.81325 7.99927C10.0958 7.5764 10.4974 7.24681 10.9673 7.05218C11.4371 6.85756 11.9542 6.80664 12.453 6.90586C12.9518 7.00507 13.41 7.24998 13.7696 7.6096C14.1292 7.96922 14.3741 8.42741 14.4733 8.92621C14.5726 9.42502 14.5216 9.94205 14.327 10.4119C14.1324 10.8818 13.8028 11.2834 13.3799 11.5659C12.9571 11.8485 12.4599 11.9993 11.9513 11.9993L11.9513 13.7136" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M11.951 17.9997C11.7143 17.9997 11.5225 17.8078 11.5225 17.5711C11.5225 17.3345 11.7143 17.1426 11.951 17.1426" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M11.9512 17.9997C12.1879 17.9997 12.3797 17.8078 12.3797 17.5711C12.3797 17.3345 12.1879 17.1426 11.9512 17.1426" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </Box>
);

/**
 * Kestrix Temperature Icon - Thermometer/temperature icon
 */
const KestrixTemperatureIcon = ({ size = 40, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 41 40"
    fill="none"
    {...props}
  >
    <path 
      d="M28.3028 10.4716L28.8396 9.90053L28.8396 9.90051L28.3028 10.4716ZM28.4063 13.7949L28.9773 14.3317L28.9774 14.3316L28.4063 13.7949ZM19.9914 22.7469L19.4204 22.2101C19.2133 22.4304 19.1514 22.7501 19.2613 23.0318L19.9914 22.7469ZM18.8782 28.5024L19.4492 29.0392L19.4492 29.0391L18.8782 28.5024ZM11.1244 28.7421L10.5876 29.3132L10.5877 29.3132L11.1244 28.7421ZM10.8839 20.9877L10.3129 20.451L10.3128 20.451L10.8839 20.9877ZM16.568 19.5235L16.3276 20.2694C16.6158 20.3623 16.9316 20.2809 17.139 20.0603L16.568 19.5235ZM24.9802 10.5743L24.4092 10.0375L24.4092 10.0376L24.9802 10.5743ZM28.3028 10.4716L27.7661 11.0426C28.397 11.6357 28.4278 12.6276 27.8352 13.2581L28.4063 13.7949L28.9774 14.3316C30.1633 13.0699 30.1008 11.0861 28.8396 9.90053L28.3028 10.4716ZM28.4063 13.7949L27.8353 13.2581L19.4204 22.2101L19.9914 22.7469L20.5624 23.2837L28.9773 14.3317L28.4063 13.7949ZM19.9914 22.7469L19.2613 23.0318C19.9009 24.6709 19.5902 26.6007 18.3071 27.9656L18.8782 28.5024L19.4492 29.0391C21.1618 27.2172 21.5725 24.6431 20.7215 22.462L19.9914 22.7469ZM18.8782 28.5024L18.3072 27.9655C16.5285 29.8574 13.5533 29.9495 11.6612 28.1711L11.1244 28.7421L10.5877 29.3132C13.1106 31.6844 17.0776 31.5617 19.4492 29.0392L18.8782 28.5024ZM11.1244 28.7421L11.6612 28.1711C9.76899 26.3924 9.67664 23.4166 11.455 21.5245L10.8839 20.9877L10.3128 20.451C7.94154 22.9741 8.06486 26.9418 10.5876 29.3132L11.1244 28.7421ZM10.8839 20.9877L11.4549 21.5245C12.7397 20.1577 14.6504 19.729 16.3276 20.2694L16.568 19.5235L16.8083 18.7775C14.5766 18.0584 12.0278 18.6265 10.3129 20.451L10.8839 20.9877ZM16.568 19.5235L17.139 20.0603L25.5512 11.1111L24.9802 10.5743L24.4092 10.0376L15.9969 18.9867L16.568 19.5235ZM24.9802 10.5743L25.5512 11.1111C26.1441 10.4805 27.1354 10.4498 27.7661 11.0426L28.3028 10.4716L28.8396 9.90051C27.5781 8.71483 25.595 8.77625 24.4092 10.0375L24.9802 10.5743Z" 
      fill={color}
    />
    <path 
      d="M14.3456 25.3183L16.4927 23.0342" 
      stroke={colors.text.subtitle} 
      strokeWidth="1.56743" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </Box>
);

/**
 * Kestrix Wall Icon - Building wall/construction icon
 */
const KestrixWallIcon = ({ size = 24, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <rect 
      x="3" 
      y="3" 
      width="18" 
      height="18" 
      rx="2" 
      ry="2" 
      stroke={color} 
      strokeWidth="1.5"
    />
  </Box>
);

/**
 * Kestrix Door Icon - Door/entrance icon
 */
const KestrixDoorIcon = ({ size = 24, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 18 24"
    fill="none"
    {...props}
  >
    <path 
      d="M14.5629 0.857422H2.56292C1.61614 0.857422 0.848633 1.62493 0.848633 2.57171V21.4289C0.848633 22.3756 1.61614 23.1431 2.56292 23.1431H14.5629C15.5097 23.1431 16.2772 22.3756 16.2772 21.4289V2.57171C16.2772 1.62493 15.5097 0.857422 14.5629 0.857422Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M10.2773 12.8574H11.9916" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </Box>
);

/**
 * Kestrix Window Icon - Window icon
 */
const KestrixWindowIcon = ({ size = 24, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 23 22"
    fill="none"
    {...props}
  >
    <path 
      d="M22.283 9.33376V20.2986C22.283 20.8461 21.8391 21.2899 21.2916 21.2899H11.7064M22.283 9.33376C20.9204 3.65533 16.3058 0.781262 11.7064 0.711271M22.283 9.33376H1.12988M1.12988 9.33376V20.2986C1.12988 20.8461 1.57372 21.2899 2.12123 21.2899H11.7064M1.12988 9.33376C2.2937 3.5138 6.99206 0.63953 11.7064 0.711271M11.7064 0.711271V21.2899" 
      stroke={color} 
      strokeWidth="1.37955"
    />
  </Box>
);

/**
 * Kestrix Roof Icon - Roof icon
 */
const KestrixRoofIcon = ({ size = 24, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path 
      d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" 
      stroke={color} 
      strokeWidth="1.5"
    />
  </Box>
);

/**
 * Kestrix Group Icon - Group/collection icon
 */
const KestrixGroupIcon = ({ size = 24, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path 
      d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <circle 
      cx="9" 
      cy="7" 
      r="4" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </Box>
);

// Export all SVG icons
export {
  KestrixDoorIcon,
  KestrixGroupIcon,
  KestrixHomeIcon,
  KestrixQuestionIcon,
  KestrixRoofIcon,
  KestrixTemperatureIcon,
  KestrixWallIcon,
  KestrixWindowIcon};

/**
 * Toolbar Icons used in KestrixFloatingToolbar
 */

export const KestrixThermalModeIcon = ({ size = 48, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 40 40" fill="none" {...props}>
    <path d="M18.1165 9.09461C18.0128 9.03269 17.8943 9 17.7736 9C17.6529 9 17.5344 9.03269 17.4307 9.09461C17.3453 9.16234 17.2876 9.25908 17.2686 9.3665C17.2497 9.47391 17.2707 9.58455 17.3279 9.67746C19.4365 13.3289 19.8822 18.3003 17.0022 21.0432C15.8941 20.112 15.0144 18.9391 14.4307 17.6146C13.3657 18.2061 12.4831 19.0781 11.8786 20.1358C11.2742 21.1936 10.9711 22.3967 11.0022 23.6146C11.0456 24.6882 11.3034 25.7423 11.7604 26.7148C12.2173 27.6873 12.8641 28.5586 13.6628 29.2774C14.4614 29.9962 15.3958 30.548 16.4109 30.9003C17.426 31.2526 18.5013 31.3984 19.5736 31.3289C25.0936 31.3289 27.9565 27.9003 28.145 23.6146C28.3679 18.4717 24.7165 12.146 18.1165 9.09461Z" stroke={color} strokeWidth="2" fill="none"/>
  </Box>
);

export const KestrixMeasurementModeIcon = ({ size = 24, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 25" fill="none" {...props}>
    <path d="M16.2358 1.35016L0.804688 16.7812L7.66564 23.6422L23.0967 8.21112L16.2358 1.35016Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.8008 4.78516L15.3722 7.35658" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8.51562 9.07422L11.0871 11.6456" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.23047 13.3594L6.8019 15.9308" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Box>
);

export const KestrixEyeIcon = ({ size = 24, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke={color} strokeWidth="2"/>
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="2"/>
  </Box>
);

export const KestrixEyeSlashIcon = ({ size = 24, color = colors.base?.KestrixThermalMode || '#FF8A00', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="1" y1="1" x2="23" y2="23" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Box>
);

export const KestrixThermalAnomalyIcon = ({ size = 34, ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 23 23" fill="none" {...props}>
    <circle cx="11.5" cy="11.5" r="10.75" fill="#9B59B6" stroke="white" strokeWidth="1.5"/>
    <path d="M7.57107 8.28261C7.49231 9.12274 7.43704 10.6095 7.9331 11.2424C7.9331 11.2424 7.69958 9.60913 9.79299 7.55993C10.6359 6.735 10.8307 5.61299 10.5364 4.77148C10.3692 4.29476 10.0638 3.90095 9.79852 3.62597C9.64376 3.4643 9.76259 3.19761 9.98783 3.20729C11.3503 3.26809 13.5584 3.6467 14.4966 6.00127C14.9084 7.03485 14.9388 8.10298 14.7426 9.18907C14.6182 9.88273 14.176 11.4248 15.1847 11.6141C15.9047 11.7495 16.2529 11.1775 16.409 10.7657C16.474 10.5943 16.6992 10.5515 16.8208 10.6883C18.0368 12.0715 18.1404 13.7006 17.8889 15.1031C17.4025 17.8142 14.6569 19.7874 11.9292 19.7874C8.52174 19.7874 5.80929 17.8377 5.10595 14.3086C4.82269 12.884 4.96639 10.0651 7.16344 8.07534C7.32649 7.92611 7.59318 8.05876 7.57107 8.28261Z" fill="url(#t1)"/>
    <path d="M13.174 13.3556C11.918 11.7389 12.4804 9.89423 12.7885 9.15912C12.83 9.06239 12.7194 8.97119 12.6324 9.03061C12.0921 9.39817 10.9853 10.2632 10.4699 11.4805C9.77207 13.1262 9.82182 13.9318 10.235 14.9157C10.4837 15.5085 10.1949 15.6342 10.0498 15.6563C9.90887 15.6784 9.77898 15.5844 9.67535 15.4863C9.37826 15.2031 9.16408 14.8341 9.06183 14.4362C9.03972 14.3505 8.9278 14.327 8.87667 14.3975C8.48977 14.9322 8.28941 15.7903 8.27974 16.3969C8.24934 18.272 9.79833 19.792 11.672 19.792C14.0335 19.792 15.7539 17.1804 14.3969 14.9972C14.0031 14.3616 13.6328 13.9456 13.174 13.3556Z" fill="url(#t2)"/>
    <defs>
      <radialGradient id="t1" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(11.2544 19.8304) rotate(-179.751) scale(9.75375 16.0039)">
        <stop offset="0.3144" stopColor="#FF9800"/>
        <stop offset="0.6616" stopColor="#FF6D00"/>
        <stop offset="0.9715" stopColor="#F44336"/>
      </radialGradient>
      <radialGradient id="t2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(11.8019 10.1276) rotate(90.5787) scale(10.2054 7.68033)">
        <stop offset="0.2141" stopColor="#FFF176"/>
        <stop offset="0.3275" stopColor="#FFF27D"/>
        <stop offset="0.4868" stopColor="#FFF48F"/>
        <stop offset="0.6722" stopColor="#FFF7AD"/>
        <stop offset="0.7931" stopColor="#FFF9C4"/>
        <stop offset="0.8221" stopColor="#FFF8BD" stopOpacity="0.804"/>
        <stop offset="0.8627" stopColor="#FFF6AB" stopOpacity="0.529"/>
        <stop offset="0.9101" stopColor="#FFF38D" stopOpacity="0.2088"/>
        <stop offset="0.9409" stopColor="#FFF176" stopOpacity="0"/>
      </radialGradient>
    </defs>
  </Box>
);

export const KestrixFixturesIcon = ({ size = 34, ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 23 23" fill="none" {...props}>
    <g clipPath="url(#clip0_2634_33053)">
      <g filter="url(#filter0_d_2634_33053)">
        <circle cx="11.5" cy="11.5" r="11.5" fill="#FFB849"/>
        <circle cx="11.5" cy="11.5" r="10.75" stroke="white" strokeWidth="1.5"/>
      </g>
      <g clipPath="url(#clip1_2634_33053)">
        <path d="M5.48438 12.3152C5.48438 12.3152 5.48641 12.6971 5.48945 12.7672C5.49453 12.9033 5.56969 12.9652 5.71797 12.9622C5.86625 12.9581 9.99172 13.0059 9.99172 13.0059L10.0588 11.7109L5.48438 12.3152Z" fill="#A06841"/>
        <path d="M12.6562 12.1378L12.9152 12.9716C12.9152 12.9716 17.0854 12.9716 17.2388 12.9798C17.3921 12.9879 17.4734 12.931 17.4815 12.7858C17.4896 12.6395 17.5038 12.0525 17.5038 12.0525L14.5829 11.6523L12.6562 12.1378Z" fill="#A06841"/>
        <path d="M9.81271 16.5848C9.81271 16.5848 9.80966 17.1433 9.80865 17.2571C9.80662 17.4714 9.86146 17.6501 10.2911 17.6501C10.7207 17.6501 12.4615 17.642 12.6392 17.6339C12.8169 17.6258 12.9581 17.5293 12.9662 17.3272C12.9743 17.1251 13.0201 16.3552 12.9388 16.3633C12.8576 16.3715 11.0518 16.3715 11.0518 16.3715L9.81271 16.5848Z" fill="#A06841"/>
        <path d="M5.79994 9.93716C5.5572 9.9463 5.4922 10.0347 5.48408 10.3018C5.47595 10.5689 5.48408 12.0588 5.48408 12.1969C5.48408 12.335 5.4922 12.5372 5.66994 12.5453C5.84767 12.5534 9.81572 12.5534 9.81572 12.5534C9.81572 12.5534 9.81572 16.3671 9.81572 16.5854C9.81572 16.8038 9.9772 17.079 10.4149 17.1115C10.8527 17.144 12.1232 17.1278 12.4228 17.1278C12.7224 17.1278 12.9652 17.014 12.9814 16.755C12.9977 16.4961 12.9977 12.5524 12.9977 12.5524C12.9977 12.5524 16.9566 12.529 17.1516 12.528C17.4268 12.527 17.4999 12.3747 17.5081 12.0019C17.5162 11.6292 17.4918 10.4714 17.4918 10.2693C17.4918 10.0672 17.3618 9.94528 17.0541 9.96153C16.7463 9.97778 12.957 9.94528 12.957 9.94528C12.957 9.94528 12.957 5.88278 12.957 5.6888C12.957 5.49481 12.8819 5.38411 12.5518 5.38716C12.3253 5.38919 10.3225 5.38309 10.1519 5.37903C9.93658 5.37395 9.83806 5.52427 9.8401 5.767C9.84213 6.00161 9.82385 9.95341 9.82385 9.95341C9.82385 9.95341 6.0183 9.92903 5.79994 9.93716Z" fill="#616266"/>
        <path d="M5.98528 10.3938C5.82379 10.5716 5.79129 11.6248 6.08278 11.6248C6.40879 11.6248 6.30114 11.1149 6.747 10.9443C7.19286 10.7737 7.40106 10.6853 7.41122 10.4994C7.42645 10.1988 6.16403 10.1968 5.98528 10.3938Z" fill="#9A9A9A"/>
        <path d="M11.6539 5.918C11.6671 6.13229 11.1156 6.2237 10.8992 6.43089C10.6819 6.63808 10.5438 6.96917 10.3813 6.96409C10.2188 6.95901 10.105 6.65331 10.1446 6.308C10.1842 5.96268 10.4757 5.7555 10.9094 5.72604C11.3431 5.69659 11.6437 5.7555 11.6539 5.918Z" fill="#9A9A9A"/>
      </g>
    </g>
    <defs>
      <filter id="filter0_d_2634_33053" x="-18" y="-14" width="59" height="59" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="4"/>
        <feGaussianBlur stdDeviation="9"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2634_33053"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2634_33053" result="shape"/>
      </filter>
      <clipPath id="clip0_2634_33053">
        <rect width="23" height="23" rx="11.5" fill="white"/>
      </clipPath>
      <clipPath id="clip1_2634_33053">
        <rect width="13" height="13" fill="white" transform="translate(5 5)"/>
      </clipPath>
    </defs>
  </Box>
);

export const KestrixObstructionsIcon = ({ size = 34, ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 23 23" fill="none" {...props}>
    <circle cx="11.5" cy="11.5" r="10.75" fill="#3A67B5" stroke="white" strokeWidth="1.5"/>
    <path d="M11.4872 4.74757L6.42615 14.8697C6.23734 15.2609 6.40085 15.731 6.79113 15.9198C6.89625 15.9705 7.01012 15.9977 7.12692 15.9987H17.249C17.6831 15.9948 18.0325 15.6395 18.0286 15.2055C18.0276 15.0896 18.0004 14.9748 17.9498 14.8697L12.8887 4.74757C12.6931 4.36021 12.2201 4.20546 11.8337 4.40109C11.6838 4.477 11.5631 4.59866 11.4872 4.74757Z" fill="#F2A600"/>
    <path d="M11.1363 5.45642L6.38667 14.9362C6.20856 15.3031 6.36233 15.745 6.72926 15.9231C6.82561 15.9698 6.9317 15.9951 7.03876 15.997H16.5282C16.9341 15.9931 17.2601 15.6612 17.2562 15.2554C17.2553 15.1444 17.229 15.0354 17.1803 14.9362L12.4405 5.45642C12.2662 5.09631 11.8341 4.94545 11.474 5.11869C11.3261 5.19072 11.2073 5.30946 11.1363 5.45642Z" fill="#FFCC32"/>
    <g opacity="0.2">
      <path opacity="0.2" d="M12.1865 7.24219C12.6342 7.24219 12.9944 7.6023 12.9652 8.02081L12.6342 12.6925C12.5973 12.9398 12.3676 13.1111 12.1203 13.0741C11.9228 13.0449 11.768 12.8901 11.7388 12.6925L11.4079 8.02081C11.3787 7.6023 11.7388 7.24219 12.1865 7.24219Z" fill="#424242"/>
      <path opacity="0.2" d="M12.1855 13.4727C12.5077 13.4727 12.7695 13.7345 12.7695 14.0566C12.7695 14.3788 12.5077 14.6406 12.1855 14.6406C11.8634 14.6406 11.6016 14.3788 11.6016 14.0566C11.6016 13.7345 11.8634 13.4727 12.1855 13.4727Z" fill="#424242"/>
    </g>
    <path d="M12.1865 7.24219C12.6342 7.24219 12.9944 7.6023 12.9652 8.02081L12.6342 12.6925C12.5973 12.9398 12.3676 13.1111 12.1203 13.0741C11.9228 13.0449 11.768 12.8901 11.7388 12.6925L11.4079 8.02081C11.3787 7.6023 11.7388 7.24219 12.1865 7.24219Z" fill="url(#paint0_linear_2634_33092)"/>
    <path d="M12.1855 14.6406C12.508 14.6406 12.7695 14.3791 12.7695 14.0566C12.7695 13.7341 12.508 13.4727 12.1855 13.4727C11.863 13.4727 11.6016 13.7341 11.6016 14.0566C11.6016 14.3791 11.863 14.6406 12.1855 14.6406Z" fill="url(#paint1_linear_2634_33092)"/>
    <path d="M11.136 6.16729C11.0192 6.31328 9.05321 10.1577 9.05321 10.1577C9.05321 10.1577 8.87801 10.4497 9.12133 10.6152C9.34519 10.7709 9.54958 10.586 9.63717 10.44C9.72477 10.294 11.5059 6.84859 11.574 6.68313C11.6324 6.50113 11.5915 6.30258 11.4669 6.15756C11.3404 6.04077 11.2139 6.06023 11.136 6.16729Z" fill="#FFF170"/>
    <path d="M8.97743 11.5838C9.15482 11.5838 9.29861 11.44 9.29861 11.2626C9.29861 11.0852 9.15482 10.9414 8.97743 10.9414C8.80005 10.9414 8.65625 11.0852 8.65625 11.2626C8.65625 11.44 8.80005 11.5838 8.97743 11.5838Z" fill="#FFF170"/>
    <defs>
      <linearGradient id="paint0_linear_2634_33092" x1="12.1865" y1="7.07185" x2="12.1865" y2="14.7306" gradientUnits="userSpaceOnUse">
        <stop stopColor="#424242"/>
        <stop offset="1" stopColor="#212121"/>
      </linearGradient>
      <linearGradient id="paint1_linear_2634_33092" x1="12.1855" y1="7.46071" x2="12.1855" y2="15.1194" gradientUnits="userSpaceOnUse">
        <stop stopColor="#424242"/>
        <stop offset="1" stopColor="#212121"/>
      </linearGradient>
    </defs>
  </Box>
);

export const KestrixHeatPumpIcon = ({ size = 24, accentColor = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke={accentColor}/>
    <circle cx="11.9996" cy="12.0016" r="7.9" stroke={accentColor}/>
    <circle cx="12.0008" cy="12.0008" r="1.2" stroke={accentColor}/>
    <path d="M12.9004 11.1041C12.5012 10.8074 11.8455 10.5246 11.1004 11.1041L9.90039 6.59924C9.90039 3.89928 14.1004 3.89915 14.1004 6.59924L12.9004 11.1041Z" stroke={accentColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.0993 11.1008C10.8026 11.4999 10.5198 12.1557 11.0993 12.9008L6.59922 13.8008C3.89927 13.8008 3.89915 10.2008 6.59923 10.2008L11.0993 11.1008Z" stroke={accentColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.9749 11.1008C13.2716 11.4999 13.5544 12.1557 12.9749 12.9008L17.475 13.8008C20.1749 13.8008 20.1751 10.2008 17.475 10.2008L12.9749 11.1008Z" stroke={accentColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.9004 13.199C12.2178 13.1682 11.8268 13.1674 11.1004 13.199L9.90039 17.1039C9.90039 19.8038 14.1004 19.804 14.1004 17.1039L12.9004 13.199Z" stroke={accentColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Box>
);

export const KestrixSolarIcon = ({ size = 24, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill="none" {...props}>
    <path
      d="M8 0.666667V2M8 14V15.3333M2.81333 2.81333L3.76 3.76M12.24 12.24L13.1867 13.1867M0.666667 8H2M14 8H15.3333M2.81333 13.1867L3.76 12.24M12.24 3.76L13.1867 2.81333M11.3333 8C11.3333 9.84095 9.84095 11.3333 8 11.3333C6.15905 11.3333 4.66667 9.84095 4.66667 8C4.66667 6.15905 6.15905 4.66667 8 4.66667C9.84095 4.66667 11.3333 6.15905 11.3333 8Z"
      stroke={color}
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixFeaturesIcon = ({ size = 24, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 25 24" fill="none" {...props}>
    <path d="M15.5138 6.29974C15.3306 6.48666 15.228 6.73798 15.228 6.99974C15.228 7.26149 15.3306 7.51281 15.5138 7.69974L17.1138 9.29974C17.3007 9.48296 17.5521 9.58559 17.8138 9.58559C18.0756 9.58559 18.3269 9.48296 18.5138 9.29974L22.2838 5.52974C22.7867 6.64092 22.9389 7.87897 22.7203 9.07888C22.5017 10.2788 21.9225 11.3836 21.0601 12.246C20.1977 13.1084 19.0929 13.6876 17.893 13.9062C16.6931 14.1248 15.455 13.9726 14.3438 13.4697L7.43382 20.3797C7.036 20.7776 6.49643 21.0011 5.93382 21.0011C5.37121 21.0011 4.83164 20.7776 4.43382 20.3797C4.036 19.9819 3.8125 19.4423 3.8125 18.8797C3.8125 18.3171 4.036 17.7776 4.43382 17.3797L11.3438 10.4697C10.841 9.35855 10.6887 8.1205 10.9074 6.92059C11.126 5.72068 11.7051 4.61589 12.5675 3.75346C13.43 2.89102 14.5348 2.3119 15.7347 2.09328C16.9346 1.87465 18.1726 2.0269 19.2838 2.52974L15.5238 6.28974L15.5138 6.29974Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Box>
);

export const KestrixChevronLeftIcon = ({ size = 16, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    {...props}
  >
    <path
      d="M10 4L6 8L10 12"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixChevronDownIcon = ({ size = 16, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    {...props}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixChevronRightIcon = ({ size = 16, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    {...props}
  >
    <path
      d="M6 4L10 8L6 12"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixCheckCircleIcon = ({ size = 14, color = colors.semantic?.success?.main || '#45B83D', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={color} {...props}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </Box>
);

export const KestrixErrorCircleIcon = ({
  size = 14,
  color = colorTokens.semantic.status.error,
  ...props
}) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={color} {...props}>
    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
  </Box>
);

/** Warning triangle from KDS helper-message (Figma 336:1135). Stroke follows `color`. */
export const KestrixWarningTriangleIcon = ({
  size = 12,
  color = colorTokens.semantic.status.warningHighEmphasis.border,
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 12.6377 12.6378"
    fill="none"
    overflow="visible"
    {...props}
  >
    <path
      d="M6.31883 4.60203V7.17346"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.31884 10.1735C6.55553 10.1735 6.74741 9.98158 6.74741 9.74489C6.74741 9.5082 6.55553 9.31632 6.31884 9.31632C6.08214 9.31632 5.89026 9.5082 5.89026 9.74489C5.89026 9.98158 6.08214 10.1735 6.31884 10.1735Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.08169 1.21632C7.00976 1.07592 6.90048 0.958091 6.76588 0.875813C6.63128 0.793536 6.47659 0.75 6.31884 0.75C6.16108 0.75 6.00639 0.793536 5.87179 0.875813C5.73719 0.958091 5.62791 1.07592 5.55598 1.21632L0.841692 10.6449C0.775941 10.7754 0.744636 10.9205 0.750751 11.0665C0.756866 11.2125 0.800198 11.3545 0.876632 11.479C0.953065 11.6035 1.06006 11.7064 1.18746 11.778C1.31486 11.8495 1.45844 11.8873 1.60455 11.8877H11.0331C11.1792 11.8873 11.3228 11.8495 11.4502 11.778C11.5776 11.7064 11.6846 11.6035 11.761 11.479C11.8375 11.3545 11.8808 11.2125 11.8869 11.0665C11.893 10.9205 11.8617 10.7754 11.796 10.6449L7.08169 1.21632Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixInfoCircleIcon = ({ size = 14, color = colors.text?.caption || '#6B5580', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={color} {...props}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </Box>
);

const NOTICE_CIRCLE_VIEWBOX = '0 0 14.8333 14.8333'
const NOTICE_CIRCLE_PATH =
  'M14.0833 7.41667C14.0833 11.0986 11.0986 14.0833 7.41667 14.0833C3.73477 14.0833 0.75 11.0986 0.75 7.41667C0.75 3.73477 3.73477 0.75 7.41667 0.75C11.0986 0.75 14.0833 3.73477 14.0833 7.41667Z'

const noticeStrokeProps = (color) => ({
  stroke: color,
  strokeWidth: '1.5',
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
})

/** Outline info (i in circle). Used by KestrixNoticeBanner. */
export const KestrixNoticeInfoIcon = ({ size = 16, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox={NOTICE_CIRCLE_VIEWBOX}
    fill="none"
    {...props}
  >
    <path d="M7.41667 10.0833V7.41667M7.41667 4.75H7.42333" {...noticeStrokeProps(color)} />
    <path d={NOTICE_CIRCLE_PATH} {...noticeStrokeProps(color)} />
  </Box>
)

/** Outline warning (exclamation in triangle). Used by KestrixNoticeBanner. */
export const KestrixNoticeWarningIcon = ({ size = 16, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 15.4307 13.5685"
    fill="none"
    {...props}
  >
    <path
      d="M7.71535 4.8185V7.48516M7.71535 10.1518H7.72201M6.57535 1.39183L0.928679 10.8185C0.812258 11.0201 0.750657 11.2487 0.750005 11.4815C0.749353 11.7143 0.809673 11.9432 0.924964 12.1455C1.04025 12.3478 1.2065 12.5163 1.40715 12.6344C1.60781 12.7525 1.83588 12.8159 2.06868 12.8185H13.362C13.5948 12.8159 13.8229 12.7525 14.0235 12.6344C14.2242 12.5163 14.3904 12.3478 14.5057 12.1455C14.621 11.9432 14.6813 11.7143 14.6807 11.4815C14.68 11.2487 14.6184 11.0201 14.502 10.8185L8.85535 1.39183C8.7365 1.1959 8.56916 1.03391 8.36948 0.921485C8.16979 0.809061 7.9445 0.75 7.71535 0.75C7.48619 0.75 7.2609 0.809061 7.06122 0.921485C6.86153 1.03391 6.69419 1.1959 6.57535 1.39183Z"
      {...noticeStrokeProps(color)}
    />
  </Box>
)

/** Outline success (check in circle). Used by KestrixNoticeBanner. */
export const KestrixNoticeSuccessIcon = ({ size = 16, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox={NOTICE_CIRCLE_VIEWBOX}
    fill="none"
    {...props}
  >
    <path d="M5.25 7.5L6.75 9L9.58333 5.75" {...noticeStrokeProps(color)} />
    <path d={NOTICE_CIRCLE_PATH} {...noticeStrokeProps(color)} />
  </Box>
)

/** Outline error (x in circle). Used by KestrixNoticeBanner. */
export const KestrixNoticeErrorIcon = ({ size = 16, color = colors.text.title, ...props }) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox={NOTICE_CIRCLE_VIEWBOX}
    fill="none"
    {...props}
  >
    <path
      d="M9.41667 5.41667L5.41667 9.41667M5.41667 5.41667L9.41667 9.41667"
      {...noticeStrokeProps(color)}
    />
    <path d={NOTICE_CIRCLE_PATH} {...noticeStrokeProps(color)} />
  </Box>
)

export const KestrixComparisonIcon = ({ size = 24, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box 
    component="svg" 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 40 40" 
    fill="none" 
    sx={{ 
      backgroundColor: 'transparent',
      background: 'none'
    }}
    {...props}
  >
    <path d="M34.7365 14.1184H19.9993M19.9993 14.1184L27.3679 22.3539M19.9993 14.1184L27.3679 5.88281" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.26329 25.884H20.0005M20.0005 25.884L12.6319 34.1196M20.0005 25.884L12.6319 17.6484" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Box>
);

export const KestrixArrowDownIcon = ({ size = 14, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 14 14" fill="none" {...props}>
    <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

export const KestrixArrowUpIcon = ({ size = 14, color = colors.base?.KestrixIconColour || '#5A5555', ...props }) => (
  <Box component="svg" xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 14 14" fill="none" {...props}>
    <path d="M3.5 8.75L7 5.25L10.5 8.75" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

const COLUMNS_GLYPH_SIZE = 16.3571
const COLUMNS_INSET_SCALE = 12 / COLUMNS_GLYPH_SIZE

/**
 * Three-column layout glyph from Figma toolbar Columns
 * (`interface-layout-three-columns`, node 224:29134).
 * Drawn in the same 16×16 frame and 2px inset as {@link FigmaToolbarGlyph}.
 */
export const KestrixColumnsIcon = ({
  size = 16,
  color = 'currentColor',
  sx = {},
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    data-testid="kestrix-columns-icon"
    sx={{
      width: `${size}px`,
      height: `${size}px`,
      flexShrink: 0,
      color,
      display: 'block',
      ...sx
    }}
    {...props}
  >
    <g transform={`translate(2 2) scale(${COLUMNS_INSET_SCALE})`}>
      <path
        d="M14.4643 0.75H1.89286C1.26167 0.75 0.75 1.26167 0.75 1.89286V14.4643C0.75 15.0955 1.26167 15.6071 1.89286 15.6071H14.4643C15.0955 15.6071 15.6071 15.0955 15.6071 14.4643V1.89286C15.6071 1.26167 15.0955 0.75 14.4643 0.75Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.32143 0.75V15.6071"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.0357 0.75V15.6071"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </Box>
);

const figmaToolbarGlyphSx = (size, color, sx) => ({
  width: size,
  height: size,
  flexShrink: 0,
  color,
  display: 'block',
  ...sx
});

const FigmaToolbarGlyph = ({
  size = 16,
  color = 'currentColor',
  path,
  testId,
  sx,
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    data-testid={testId}
    sx={figmaToolbarGlyphSx(size, color, sx)}
    {...props}
  >
    <path
      d={path}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

/** Download tray from Figma Export (node 224:29134). */
export const KestrixDownloadIcon = (props) => (
  <FigmaToolbarGlyph
    testId="kestrix-download-icon"
    path="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10M11.3333 6.66667L8 10L4.66667 6.66667M8 10V2"
    {...props}
  />
);

/**
 * 16×16 trash from Figma Applied Sort (node 305:48545). Lid + can only —
 * no inner hatch lines. Stroke is currentColor; callers set the error token.
 */
export const KestrixTrashIcon = ({
  size = 16,
  color = 'currentColor',
  sx,
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    data-testid="kestrix-trash-icon"
    sx={figmaToolbarGlyphSx(size, color, sx)}
    {...props}
  >
    <path
      d="M12.6667 3.99968V13.3338C12.6667 13.6874 12.5262 14.0266 12.2761 14.2766C12.0261 14.5267 11.687 14.6672 11.3333 14.6672H4.66667C4.31304 14.6672 3.97391 14.5267 3.72386 14.2766C3.47381 14.0266 3.33333 13.6874 3.33333 13.3338V3.99968M2 3.99968H14M5.33333 3.99968V2.66624C5.33333 2.31259 5.47381 1.97342 5.72386 1.72336C5.97391 1.47329 6.31304 1.3328 6.66667 1.3328H9.33333C9.68696 1.3328 10.0261 1.47329 10.2761 1.72336C10.5262 1.97342 10.6667 2.31259 10.6667 2.66624V3.99968"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </Box>
);

/** Bookmark from Figma Save as new view (node 224:29134). */
export const KestrixBookmarkIcon = (props) => (
  <FigmaToolbarGlyph
    testId="kestrix-bookmark-icon"
    path="M12.6667 14L8 10.6667L3.33333 14V3.33333C3.33333 2.97971 3.47381 2.64057 3.72386 2.39052C3.97391 2.14048 4.31304 2 4.66667 2H11.3333C11.687 2 12.0261 2.14048 12.2761 2.39052C12.5262 2.64057 12.6667 2.97971 12.6667 3.33333V14Z"
    {...props}
  />
);

/** Magnifying glass from Figma Search field (node 44:21291). */
export const KestrixSearchIcon = (props) => (
  <FigmaToolbarGlyph
    testId="kestrix-search-icon"
    path="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.27885 10.27885 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.27885 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.27885 2 12.6667 4.38781 12.6667 7.33333Z"
    {...props}
  />
);

/** Three decreasing lines from Figma Filter (node 326:61360). */
export const KestrixFilterLinesIcon = (props) => (
  <FigmaToolbarGlyph
    testId="kestrix-filter-lines-icon"
    path="M4 8H12M2 4H14M6 12H10"
    {...props}
  />
);

/**
 * Stateful arrow that flips between {@link KestrixArrowDownIcon} and {@link KestrixArrowUpIcon}.
 */
export const KestrixDropdownArrowIcon = ({ disabled = false, isOpen = false }) => {
  const iconColor = disabled ? colors.text.disabled : colors.text.subtitle;
  const Icon = isOpen ? KestrixArrowUpIcon : KestrixArrowDownIcon;

  return (
    <span aria-hidden style={{ marginLeft: 12, display: 'inline-flex' }}>
      <Icon size={14} color={iconColor} />
    </span>
  );
};

const viewTabLeadingIconSx = (size, color) => ({
  fontSize: `${size}px`,
  width: `${size}px`,
  height: `${size}px`,
  color
});

const viewTabCrossfadeSx = (visible, stateTransition) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: visible ? 1 : 0,
  transition: stateTransition
});

/** View tab context menu icons (Figma view-actions-popover, node 5376:52828). */
const viewMenuIconDefaults = {
  size: 16
};

/** @param {number} size */
const viewMenuIconSx = (size) => ({
  width: size,
  height: size,
  flexShrink: 0,
  display: 'block'
});

export const KestrixRenameViewIcon = ({
  size = viewMenuIconDefaults.size,
  color = 'currentColor',
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 14.719 14.719"
    fill="none"
    sx={{ ...viewMenuIconSx(size), color }}
    {...props}
  >
    <path
      d="M10.75 1.30228C10.9251 1.12719 11.133 0.988295 11.3618 0.893534C11.5905 0.798773 11.8357 0.75 12.0834 0.75C12.331 0.75 12.5762 0.798773 12.8049 0.893534C13.0337 0.988295 13.2416 1.12719 13.4167 1.30228C13.5918 1.47738 13.7307 1.68525 13.8254 1.91402C13.9202 2.1428 13.969 2.388 13.969 2.63562C13.969 2.88324 13.9202 3.12844 13.8254 3.35721C13.7307 3.58599 13.5918 3.79386 13.4167 3.96895L4.41668 12.969L0.750017 13.969L1.75002 10.3023L10.75 1.30228Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixEditViewDescriptionIcon = ({
  size = viewMenuIconDefaults.size,
  color = 'currentColor',
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 12.1667 14.8333"
    fill="none"
    sx={{ ...viewMenuIconSx(size), color }}
    {...props}
  >
    <path
      d="M11.4167 4.75L7.41667 0.75H2.08333C1.72971 0.75 1.39057 0.890476 1.14052 1.14052C0.890476 1.39057 0.75 1.72971 0.75 2.08333V12.75C0.75 13.1036 0.890476 13.4428 1.14052 13.6928C1.39057 13.9429 1.72971 14.0833 2.08333 14.0833H10.0833C10.437 14.0833 10.7761 13.9429 11.0261 13.6928C11.2762 13.4428 11.4167 13.1036 11.4167 12.75V4.75ZM7.41667 0.75V4.75H11.4167M8.75 8.08333H3.41667M8.75 10.75H3.41667M4.75 5.41667H3.41667"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixDuplicateViewIcon = ({
  size = viewMenuIconDefaults.size,
  color = 'currentColor',
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 14.8333 14.8333"
    fill="none"
    sx={{ ...viewMenuIconSx(size), color }}
    {...props}
  >
    <path
      d="M2.75 9.41667H2.08333C1.72971 9.41667 1.39057 9.27619 1.14052 9.02614C0.890476 8.77609 0.75 8.43695 0.75 8.08333V2.08333C0.75 1.72971 0.890476 1.39057 1.14052 1.14052C1.39057 0.890476 1.72971 0.75 2.08333 0.75H8.08333C8.43695 0.75 8.77609 0.890476 9.02614 1.14052C9.27619 1.39057 9.41667 1.72971 9.41667 2.08333V2.75M6.75 5.41667H12.75C13.4864 5.41667 14.0833 6.01362 14.0833 6.75V12.75C14.0833 13.4864 13.4864 14.0833 12.75 14.0833H6.75C6.01362 14.0833 5.41667 13.4864 5.41667 12.75V6.75C5.41667 6.01362 6.01362 5.41667 6.75 5.41667Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixCopyViewLinkIcon = ({
  size = viewMenuIconDefaults.size,
  color = 'currentColor',
  ...props
}) => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 14.7581 14.7447"
    fill="none"
    sx={{ ...viewMenuIconSx(size), color }}
    {...props}
  >
    <path
      d="M6.04571 8.03904C6.33201 8.42179 6.69728 8.73849 7.11674 8.96767C7.5362 9.19684 8.00004 9.33312 8.47681 9.36726C8.95357 9.40141 9.4321 9.33262 9.87994 9.16556C10.3278 8.9985 10.7344 8.73709 11.0724 8.39904L13.0724 6.39904C13.6796 5.77037 14.0155 4.92836 14.008 4.05437C14.0004 3.18038 13.6498 2.34434 13.0318 1.72631C12.4137 1.10828 11.5777 0.757721 10.7037 0.750126C9.82972 0.742531 8.98771 1.07851 8.35904 1.68571L7.21237 2.82571M8.71237 6.70571C8.42607 6.32295 8.0608 6.00625 7.64134 5.77708C7.22188 5.54791 6.75803 5.41163 6.28127 5.37748C5.80451 5.34334 5.32598 5.41213 4.87814 5.57919C4.4303 5.74624 4.02363 6.00766 3.68571 6.34571L1.68571 8.34571C1.07851 8.97438 0.742531 9.81639 0.750126 10.6904C0.757721 11.5644 1.10828 12.4004 1.72631 13.0184C2.34434 13.6365 3.18038 13.987 4.05437 13.9946C4.92836 14.0022 5.77037 13.6662 6.39904 13.059L7.53904 11.919"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const KestrixDeleteViewIcon = ({
  size = viewMenuIconDefaults.size,
  color = 'currentColor',
  sx = {},
  ...props
}) => (
  <KestrixTrashIcon
    size={size}
    color={color}
    sx={{ ...viewMenuIconSx(size), ...sx }}
    {...props}
  />
);

const viewTabLockLeaf = {
  width: 12,
  height: 13.1667,
  viewBox: '0 0 12 13.1667',
  frame: 16
};

/**
 * Closed lock from Figma View Tab (node 183:11073). Centered in the same
 * 16×16 frame as toolbar icons so it does not optically outsize Filter.
 */
export const KestrixLockIcon = ({
  size = viewTabLockLeaf.frame,
  color = 'currentColor',
  sx,
  ...props
}) => {
  const offsetX = (viewTabLockLeaf.frame - viewTabLockLeaf.width) / 2;
  const offsetY = (viewTabLockLeaf.frame - viewTabLockLeaf.height) / 2;

  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${viewTabLockLeaf.frame} ${viewTabLockLeaf.frame}`}
      fill="none"
      data-testid="view-tab-lock-icon"
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        flexShrink: 0,
        color,
        ...sx
      }}
      {...props}
    >
      <g transform={`translate(${offsetX} ${offsetY})`}>
        <path
          d="M3.08333 6V3.66667C3.08333 2.89312 3.39062 2.15125 3.93761 1.60427C4.48459 1.05729 5.22645 0.75 6 0.75C6.77355 0.75 7.51541 1.05729 8.06239 1.60427C8.60938 2.15125 8.91667 2.89312 8.91667 3.66667V6M1.91667 6H10.0833C10.7277 6 11.25 6.52233 11.25 7.16667V11.25C11.25 11.8943 10.7277 12.4167 10.0833 12.4167H1.91667C1.27233 12.4167 0.75 11.8943 0.75 11.25V7.16667C0.75 6.52233 1.27233 6 1.91667 6Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </Box>
  );
};

const renderViewTabLeadingIconContent = ({
  isAction,
  locked,
  isNotSelected,
  isActive,
  iconSize,
  iconColor,
  primaryColor,
  secondaryColor,
  stateTransition
}) => {
  if (isAction) {
    return (
      <AddIcon
        sx={{
          ...viewTabLeadingIconSx(iconSize, primaryColor),
          transition: stateTransition,
          transform: 'rotate(0deg)'
        }}
      />
    );
  }

  if (locked) {
    return (
      <KestrixLockIcon
        size={iconSize}
        color={iconColor}
        sx={{ color: iconColor, transition: stateTransition }}
      />
    );
  }

  return (
    <>
      <Box sx={viewTabCrossfadeSx(isNotSelected, stateTransition)}>
        <TableChartOutlinedIcon
          sx={viewTabLeadingIconSx(iconSize, secondaryColor)}
        />
      </Box>
      <Box sx={viewTabCrossfadeSx(isActive, stateTransition)}>
        <TableChartOutlinedIcon
          sx={viewTabLeadingIconSx(iconSize, primaryColor)}
        />
      </Box>
    </>
  );
};

/**
 * Leading icon for KDS View Tab — crossfade between selected/unselected chart icons.
 */
export const KestrixViewTabLeadingIcon = ({
  variant,
  locked = false,
  stateTransition = 'none',
  iconSize = 16,
  secondaryColor,
  primaryColor
}) => {
  const isAction = variant === 'new-view';
  const isNotSelected = variant === 'not-selected';
  const isActive = variant === 'active' || variant === 'modified';
  const iconColor = isNotSelected ? secondaryColor : primaryColor;

  return (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: iconSize,
        height: iconSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {renderViewTabLeadingIconContent({
        isAction,
        locked,
        isNotSelected,
        isActive,
        iconSize,
        iconColor,
        primaryColor,
        secondaryColor,
        stateTransition
      })}
    </Box>
  );
};
